<script lang="ts">
  import {onMount} from 'svelte'
  import type {Language, Poem} from './types'
  import api from './api/api'
  import {t} from './i18n'
  import {user} from './stores/user'
  import Dropdown from './shared/Dropdown.svelte'
  import SmallPoem from './poems/SmallPoem.svelte'
  import Placeholder from './shared/Placeholder.svelte'
  import LoadMore from './shared/LoadMore.svelte'
  import {fromQueryParams, toQueryParams} from './utils/queryParams'
  import {listen as onPageChange} from 'svelte5-router'

  const periods = ['week', 'month', 'forever']
  $: orders = $user ? ['popular', 'recent', 'recommended'] : ['popular', 'recent']

  let poems: Poem[] | undefined
  let period = 'forever'
  let order = 'recent'
  let language = ''
  let usedLanguages: Language[] = []
  let tags: string[] = getInitialTags()
  let tag = ''

  onMount(() => {
    onPageChange(() => {
      tags = getInitialTags()
    })
  })

  function getInitialTags() {
    const urlTags = fromQueryParams(window.location.search.split('?')[1])['tags']
    if (urlTags) {
      return Array.isArray(urlTags) ? urlTags : [urlTags]
    }

    return []
  }

  const fetchUsedLanguages = async () => {
    usedLanguages = (await api.get<Language[]>('/languages/used')).sortBy(l => t.languages[l.code.toLowerCase()])
  }

  const addTag = (event: KeyboardEvent) => {
    if (event.key !== 'Enter') {
      return
    }

    if (!tag) return
    if (!tags.includes(tag)) tags = [...tags, tag]
    tag = ''
    updateUrl()
  }

  const removeTag = (tag: string) => {
    tags = tags.filter((t) => t !== tag)
    updateUrl()
  }

  function updateUrl() {
    poems = undefined

    if (tags.length > 0) {
      const url = '/explore?' + toQueryParams({tags})
      window.history.pushState({path: url}, '', url);
    } else {
      window.history.pushState({path: '/explore'}, '', '/explore');
    }
  }

  onMount(() => {
    fetchUsedLanguages()
  })

  function buildUrlParams(period: string, language: string, tags: string[]) {
    const res = {} as Record<any, any>
    if (period) res.period = period
    if (language) res.language = language
    if (tags.length) res.tags = tags
    return res
  }

  $: poemsUrlParams = toQueryParams(buildUrlParams(period, language, tags))

  $: poemsUrl = `/poems/explore?${poemsUrlParams}`
</script>

<div class="row">
  <div class="grid-section">
    <div class="heading with-filter inner-heading">
      <h2>{t.explore.title}</h2>

      <div class="right-filter">
        <Dropdown bind:value={order} options={orders.map(o => ({value: o, label: t.orders[o]}))}/>
        <Dropdown bind:value={period} options={periods.map(p => ({value: p, label: t.periods[p]}))}/>
        <Dropdown bind:value={language} options={usedLanguages.map(l => ({value: l.code, label: t.languages[l.code]}))}
                  class="ui select search dropdown" emptyOption={t.languages.prompt} autocomplete/>
      </div>
    </div>

    <div class="tag-search-wrap">
      <div class="added__tags">
        {#each tags as tag}
          <div class="added__tag_item">
            <i class="remove icon-p-close" onclick={() => removeTag(tag)}></i>
            {tag}
          </div>
        {/each}
      </div>

      <div class="tag-search">
        <label for="tagSearch" class="search-icon icon-p-lupa"></label>
        <input id="tagSearch" placeholder={t.explore.type_tags} bind:value={tag} onkeydown={addTag}/>
      </div>
    </div>

    {#if poems && poems.length}
      <div class="grid three">
        {#each poems as poem}
          <div class="grid__item">
            <SmallPoem {poem}/>
          </div>
        {/each}
      </div>
    {:else}
      <Placeholder placeholder={t.placeholder.no_poems} loading={!poems}/>
    {/if}

    <LoadMore bind:items={poems} url={poemsUrl} {order} />
  </div>
</div>
